import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, FileSpreadsheet, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorMessage } from "@/lib/api";

const FIELD_LABELS = {
  full_name: "Nama Lengkap",
  email: "Email",
  phone: "Telepon",
  nisn: "NISN",
  nip: "NIP",
  role: "Peran",
  gender: "Jenis Kelamin",
  birth_date: "Tanggal Lahir",
  student_registration_number: "NIS/NIPD",
  employee_registration_number: "NUPTK/NITK",
};

const ROLE_COLORS = {
  school_admin: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
  kepala_sekolah: "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200",
  operator: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
  guru: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
  wali_kelas: "bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-200",
  siswa: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  orang_tua: "bg-gray-100 text-gray-800 dark:bg-gray-950 dark:text-gray-200",
};

export function ImportDialog({ open, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState(null);

  const previewMut = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      return (await api.post("/school/members/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: (d) => {
      setPreview(d);
      setMapping(d.mapping);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const executeMut = useMutation({
    mutationFn: async () => (await api.post("/school/members/import/execute", {
      rows: preview.rows || preview.preview,
      mapping,
    })).data,
    onSuccess: (d) => {
      toast.success(`${d.created} anggota berhasil diimpor.`);
      if (d.errors?.length) toast.warning(`${d.errors.length} baris dilewati.`, { duration: 5000 });
      onDone?.();
      handleClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const handleClose = () => {
    setPreview(null);
    setFile(null);
    setMapping(null);
    onOpenChange(false);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.match(/\.(xlsx|ods|csv)$/i)) {
      toast.error("Format file tidak didukung. Gunakan .xlsx, .ods, atau .csv.");
      return;
    }
    setFile(f);
    previewMut.mutate(f);
  };

  const updateMapping = (field, colName) => {
    setMapping((m) => ({ ...m, [field]: colName }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl gap-0 p-0 sm:max-w-[900px]">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-lg font-bold">Impor Anggota Massal</DialogTitle>
          <DialogDescription>
            Upload file Excel (.xlsx), OpenDocument (.ods), atau CSV. Sistem akan otomatis mendeteksi kolom.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-4">
          {!preview ? (
            <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center">
              <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-sm font-medium mb-1">Pilih file spreadsheet</p>
              <p className="text-xs text-muted-foreground mb-4">Maksimum 5MB. Format: .xlsx, .ods, .csv</p>
              <input
                type="file"
                accept=".xlsx,.ods,.csv"
                onChange={handleFile}
                className="hidden"
                id="import-file-input"
                data-testid="import-file-input"
              />
              <Button asChild variant="outline" data-testid="import-file-button">
                <label htmlFor="import-file-input" className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-1" /> Pilih File
                </label>
              </Button>
              {previewMut.isPending && <p className="mt-3 text-sm text-muted-foreground">Menganalisis file...</p>}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-[#0B3D91]" />
                  <span className="text-sm font-medium">{file?.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({preview.total_rows} baris{preview.sheet_name ? ` · Sheet: ${preview.sheet_name}` : ""})
                  </span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setPreview(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="rounded-lg border bg-muted/50 p-3">
                <p className="text-xs font-semibold mb-2">Pemetaan Kolom (otomatis terdeteksi)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(mapping || {}).map(([field, colName]) => (
                    <div key={field} className="flex items-center gap-1.5 text-xs">
                      <span className="font-medium text-muted-foreground">{FIELD_LABELS[field] || field}:</span>
                      <span className={colName ? "text-foreground" : "text-red-600 font-semibold"}>
                        {colName || "Tidak terdeteksi"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <p className="text-xs font-semibold px-3 py-2 bg-muted">Pratinjau (10 baris pertama)</p>
                <div className="max-h-64 overflow-auto">
                  <div className="min-w-full inline-block align-middle">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 sticky left-0 bg-muted z-10">No.</TableHead>
                          {(mapping && Object.entries(mapping)
                            .filter(([_, colName]) => colName)
                            .map(([field, _]) => (
                              <TableHead key={field}>{FIELD_LABELS[field] || field}</TableHead>
                            )))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.preview?.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-muted-foreground sticky left-0 bg-background z-10">{i + 1}</TableCell>
                            {(mapping && Object.entries(mapping)
                              .filter(([_, colName]) => colName)
                              .map(([field, _]) => {
                                const value = row[field];
                                if (field === "role" && value) {
                                  return (
                                    <TableCell key={field}>
                                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${ROLE_COLORS[value] || "bg-gray-100"}`}>
                                        {value}
                                      </span>
                                    </TableCell>
                                  );
                                }
                                return (
                                  <TableCell key={field} className={`text-xs ${field === "nisn" || field === "nip" ? "font-mono" : ""}`}>
                                    {value || "—"}
                                  </TableCell>
                                );
                              }))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>

              {(!mapping?.full_name || !mapping?.role) && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p>Kolom <strong>Nama</strong> dan <strong>Peran</strong> wajib terdeteksi. Pastikan file memiliki kolom dengan nama yang sesuai.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={executeMut.isPending}>
            Batal
          </Button>
          {preview && (
            <Button
              onClick={() => executeMut.mutate()}
              disabled={!mapping?.full_name || !mapping?.role || executeMut.isPending}
              data-testid="import-execute-button"
            >
              {executeMut.isPending ? "Mengimpor..." : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Impor {preview.total_rows} Anggota
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
